import React, { useEffect, useMemo, useState } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import EditableTable from "../../../../../../components/Tables/EditableTable";

const createFallbackRows = (tableName) => [
  {
    id: 1,
    name: `${tableName} A`,
    value: "Sample Value 1",
    status: "Draft",
  },
  {
    id: 2,
    name: `${tableName} B`,
    value: "Sample Value 2",
    status: "Approved",
  },
];

const buildColumns = (rows) => {
  if (!rows?.length) {
    return [{ accessorKey: "id", header: "ID" }];
  }

  const baseColumns = [{ accessorKey: "id", header: "ID" }];
  const extraKeys = Object.keys(rows[0]).filter((key) => key !== "id");

  return [
    ...baseColumns,
    ...extraKeys.map((key) => ({
      accessorKey: key,
      header: key.replace(/_/g, " "),
    })),
  ];
};

export default function EditingTables({
  title = "Edit tables",
  tableOptions = [],
  dbTab,
  tableApi,
  initialTable,
  enableCreate = true,
  enableEdit = true,
  enableDelete = true,
}) {
  const [selectedTable, setSelectedTable] = useState(initialTable || tableOptions[0] || "");
  const [tableDataMap, setTableDataMap] = useState(() => {
    const starterMap = {};
    tableOptions.forEach((tableName) => {
      starterMap[tableName] = createFallbackRows(tableName);
    });
    return starterMap;
  });

  useEffect(() => {
    if (!selectedTable && tableOptions?.length) {
      setSelectedTable(tableOptions[0]);
    }
  }, [selectedTable, tableOptions]);

  const availableTables = useMemo(() => {
    if (tableOptions?.length) return tableOptions;
    return selectedTable ? [selectedTable] : [];
  }, [selectedTable, tableOptions]);

  const rows = useMemo(() => {
    if (!selectedTable) return [];
    return tableDataMap[selectedTable] || [];
  }, [selectedTable, tableDataMap]);

  const columns = useMemo(() => buildColumns(rows), [rows]);

  const tableApiAdapter = useMemo(
    () => ({
      getItems: async () => {
        if (tableApi?.getItems) {
          const result = await tableApi.getItems(selectedTable, dbTab);
          return result ?? [];
        }

        return tableDataMap[selectedTable] || createFallbackRows(selectedTable);
      },
      createItem: async (_parentId, item) => {
        const nextRows = [
          ...(tableDataMap[selectedTable] || []),
          { ...item, id: Date.now() },
        ];
        setTableDataMap((prev) => ({ ...prev, [selectedTable]: nextRows }));
        return nextRows;
      },
      updateItem: async (item) => {
        const nextRows = (tableDataMap[selectedTable] || []).map((row) =>
          row.id === item.id ? { ...row, ...item } : row
        );
        setTableDataMap((prev) => ({ ...prev, [selectedTable]: nextRows }));
        return nextRows;
      },
      deleteItem: async (id) => {
        const nextRows = (tableDataMap[selectedTable] || []).filter((row) => row.id !== id);
        setTableDataMap((prev) => ({ ...prev, [selectedTable]: nextRows }));
        return nextRows;
      },
    }),
    [dbTab, selectedTable, tableApi, tableDataMap]
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Select table</InputLabel>
          <Select
            value={selectedTable}
            label="Select table"
            onChange={(event) => setSelectedTable(event.target.value)}
          >
            {availableTables.map((tableName) => (
              <MenuItem key={tableName} value={tableName}>
                {tableName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <EditableTable
        name={selectedTable || title}
        columns={columns}
        displayMode="modal"
        api={tableApiAdapter}
        enableEditing={enableEdit}
        enableCreate={false}
        initialData={{}}
        parentId={selectedTable}
        enableDelete={enableDelete}
      />
    </Box>
  );
}
